// src/modules/ProductSelection.jsx
import React, { useState, useCallback } from 'react';
import { C } from '../constants';
import { Ic } from '../components/ui/Icons';
import ProductCatalog from './ProductCatalog.jsx';
import { MANUFACTURERS } from './productCatalog';

import chemorlLogo from '../assets/logos/chemori_logo.png';
import avaLogo from '../assets/logos/ava_logo.png';
import potterLogo from '../assets/logos/potter_logo.png';
import vesLogo from '../assets/logos/ves_logo.png';
import statxLogo from '../assets/logos/StatX_logo.png';
import protectowireLogo from '../assets/logos/protectowire_logo.png';
import thermocableLogo from '../assets/logos/thermocable_logo.png';
import macurcoLogo from '../assets/logos/macurco_logo.png';
import firerayLogo from '../assets/logos/fireray_logo.png';
import dgiLogo from '../assets/logos/dgi_logo.png';
import altronixLogo from '../assets/logos/altronix_logo.png';
import boalLogo from '../assets/logos/boal_systems_logo.png';

const LOGO_MAP = {
  chemori: chemorlLogo, ava: avaLogo, potter: potterLogo, ves: vesLogo,
  statx: statxLogo, protectowire: protectowireLogo, thermocable: thermocableLogo,
  macurco: macurcoLogo, fireray: firerayLogo, dgi: dgiLogo,
  altronix: altronixLogo, boal: boalLogo,
};

function BackButton({ onClick }) {
  return (
    <button onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit", padding: "4px 0", marginBottom: 20, transition: "color 0.2s" }}
      onMouseEnter={e => e.currentTarget.style.color = C.navy}
      onMouseLeave={e => e.currentTarget.style.color = C.t3}>
      <Ic.ChevronLeft /> Back
    </button>
  );
}

export default function ProductSelection({ userInfo, onAbandon, onUnsavedChange, onSubmitSuccess, onSubmitComplete }) {
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [quoteList, setQuoteList] = useState({});
  const [showAbandonWarning, setShowAbandonWarning] = useState(false);
  const [pendingAbandon, setPendingAbandon] = useState(null);

  const hasItems = Object.keys(quoteList).length > 0;

  const handleManufacturerClick = useCallback((mfr) => {
    if (selectedManufacturer && selectedManufacturer.id !== mfr.id && hasItems) {
      setShowAbandonWarning(true);
      setPendingAbandon({ type: 'switchMfr', mfr });
    } else {
      setSelectedManufacturer(mfr);
    }
  }, [selectedManufacturer, hasItems]);

  const handleBackToGrid = () => {
    if (hasItems) {
      setShowAbandonWarning(true);
      setPendingAbandon({ type: 'backToGrid' });
    } else {
      setSelectedManufacturer(null);
    }
  };

  const handleBackToMain = () => {
    if (hasItems || selectedManufacturer) {
      setShowAbandonWarning(true);
      setPendingAbandon({ type: 'backToMain' });
    } else {
      onAbandon && onAbandon();
    }
  };

  const handleAbandonConfirm = () => {
    setShowAbandonWarning(false);
    const p = pendingAbandon;
    setPendingAbandon(null);
    setQuoteList({});
    if (onUnsavedChange) onUnsavedChange(false);
    if (p?.type === 'switchMfr') {
      setSelectedManufacturer(p.mfr);
    } else if (p?.type === 'backToGrid') {
      setSelectedManufacturer(null);
    } else if (p?.type === 'backToMain') {
      setSelectedManufacturer(null);
      onAbandon && onAbandon();
    }
  };

  const handleAbandonCancel = () => {
    setShowAbandonWarning(false);
    setPendingAbandon(null);
  };

  const handleSetQuoteList = (updater) => {
    setQuoteList(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (onUnsavedChange) onUnsavedChange(Object.keys(next).length > 0);
      return next;
    });
  };

  if (selectedManufacturer) {
    return (
      <>
        <ProductCatalog
          manufacturer={selectedManufacturer}
          userInfo={userInfo}
          quoteList={quoteList}
          setQuoteList={handleSetQuoteList}
          onBack={handleBackToGrid}
          onSubmitSuccess={() => {
            setQuoteList({});
            if (onUnsavedChange) onUnsavedChange(false);
            onSubmitSuccess && onSubmitSuccess();
          }}
          onSubmitComplete={onSubmitComplete}
        />
        {showAbandonWarning && <AbandonModal onConfirm={handleAbandonConfirm} onCancel={handleAbandonCancel} />}
      </>
    );
  }

  return (
    <div style={{ maxWidth: 960 }}>
      <BackButton onClick={handleBackToMain} />
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.t1, margin: "0 0 6px" }}>Product Selection</h1>
        <p style={{ fontSize: 14, color: C.t2, margin: 0 }}>
          Select a manufacturer to browse products and build a quote request. Each quote request is limited to one manufacturer.
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
        {MANUFACTURERS.map((mfr) => (
          <ManufacturerCard key={mfr.id} manufacturer={mfr} logo={LOGO_MAP[mfr.logoKey]} onClick={() => handleManufacturerClick(mfr)} />
        ))}
      </div>
      {showAbandonWarning && <AbandonModal onConfirm={handleAbandonConfirm} onCancel={handleAbandonCancel} />}
    </div>
  );
}

function ManufacturerCard({ manufacturer, logo, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: hovered ? C.grayBg : C.white, border: `1px solid ${hovered ? C.navy + '30' : C.border}`, borderRadius: 14, padding: "20px 22px", cursor: "pointer", textAlign: "left", transition: "all 0.2s ease", display: "flex", flexDirection: "column", gap: 12, boxShadow: hovered ? `0 4px 20px rgba(0,0,0,0.08)` : "none", transform: hovered ? "translateY(-1px)" : "none", fontFamily: "inherit" }}>
      <div style={{ width: "100%", height: 56, display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
        <img src={logo} alt={manufacturer.name} style={{ maxHeight: 44, maxWidth: "85%", objectFit: "contain", objectPosition: "left center" }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, marginBottom: 4 }}>{manufacturer.name}</div>
        <div style={{ fontSize: 13, color: C.t2, lineHeight: 1.45 }}>{manufacturer.description}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", color: hovered ? C.navy : C.t3, transition: "color 0.2s", fontSize: 18 }}>
        <Ic.ChevronRight />
      </div>
    </button>
  );
}

function AbandonModal({ onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: "28px 32px", maxWidth: 400, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Abandon Quote List?</h3>
        <p style={{ fontSize: 14, color: C.t2, margin: "0 0 24px", lineHeight: 1.5 }}>You have items in your Quote List. Leaving now will clear your selections. Are you sure you want to continue?</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.t2, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Keep Editing</button>
          <button onClick={onConfirm} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: C.red, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Yes, Abandon</button>
        </div>
      </div>
    </div>
  );
}
